import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { ArrowRight, Globe, Smartphone, Cloud, Brain, Database, Users, TrendingUp, MessageSquare, Check, Target, Eye, Award } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Globe,
  Smartphone,
  Cloud,
  Brain,
  Database,
  Users,
  TrendingUp,
  MessageSquare
};

const ServicesPage = () => {
  const whyChooseUs = [
    'End-to-End IT Management',
    'Certified Experts',
    'Strategic Partnerships',
    'Scalable & Custom Solutions'
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#B8336A]/20 blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-[#2D6A6A]/20 blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <span>/</span>
              <span className="text-white">Services</span>
            </nav>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Enterprise IT & Software
              <span className="bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] bg-clip-text text-transparent"> Services Built for Modern Businesses</span>
            </h1>
            <p className="text-gray-300 text-lg mb-8">
              AMTB Solution delivers scalable IT and software solutions - From web and mobile development to cloud services and AI tools, we help organizations build, optimize, and scale their digital environments.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white rounded-full font-semibold hover:shadow-lg transition-all"
            >
              Request Service Consultation
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-[#B8336A]/10 to-[#2D6A6A]/10 text-sm font-medium text-[#B8336A] mb-4">
              By Professionals. For Progressive Businesses.
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Comprehensive IT & Software Services for a
              <span className="bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] bg-clip-text text-transparent"> Connected Future</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-transparent hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-[#B8336A]/10 to-[#2D6A6A]/10 flex items-center justify-center mb-5 group-hover:bg-gradient-to-r group-hover:from-[#B8336A] group-hover:to-[#2D6A6A] transition-all">
                    {Icon && <Icon size={28} className="text-[#B8336A] group-hover:text-white transition-colors" />}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    {service.category}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-[#B8336A]/10 to-[#2D6A6A]/10 text-sm font-medium text-[#B8336A] mb-4">
                Trusted by businesses, designed for performance.
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Partner with Experts Who Keep Your Technology
                <span className="bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] bg-clip-text text-transparent"> Secure and Future-Ready</span>
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                At AMTB Solution, we combine technology, strategy, and expertise to deliver results that move businesses forward. Our end-to-end approach ensures reliability, scalability, and quality — every step of the way.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] flex items-center justify-center">
                      <Check size={14} className="text-white" />
                    </div>
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600"
                alt="Team working"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission/Vision/Experience */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: 'Experience',
                subtitle: 'Delivering reliable, innovative IT solutions backed by real-world experience',
                description: 'Over a decade building software solutions for enterprise environments with practical expertise and measurable outcomes.'
              },
              {
                icon: Target,
                title: 'Mission',
                subtitle: 'Empowering businesses through technology',
                description: 'At AMTB Solution, our mission is to redefine software development by delivering cutting-edge solutions that help businesses grow and innovate.'
              },
              {
                icon: Eye,
                title: 'Vision',
                subtitle: 'Building a smarter digital tomorrow through trust and innovation',
                description: 'To empower startups and SMEs with reliable IT and software solutions—guiding them toward secure and scalable growth.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-gray-100">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-[#B8336A]/10 to-[#2D6A6A]/10 flex items-center justify-center mb-6">
                  <item.icon size={28} className="text-[#B8336A]" />
                </div>
                <span className="text-sm font-medium text-[#2D6A6A]">{item.title}</span>
                <h3 className="text-xl font-bold text-gray-900 mt-2 mb-4">{item.subtitle}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
